import { mergeAttributes, Node } from '@tiptap/core'
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react'
import ReactPlayer from 'react-player/lazy'
import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../../../firebase/FirebaseConfig'
import LinearProgress from '@mui/material/LinearProgress'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

/**
 * NodeView for the `video` extension. Three states:
 *
 *   1. `src` is set        → render the player.
 *   2. `src` empty, `transcodeId` set → subscribe to
 *      `videoTranscodes/{transcodeId}` in Firestore. While the doc reports
 *      'processing' (or doesn't exist yet) show a "Processing video…" card.
 *      When it flips to 'complete' with an `outputUrl`, persist that URL
 *      back into the node's `src` attribute (so the saved HTML reflects it)
 *      and render the player.
 *   3. `src` empty, transcode reports 'error' → show an error card.
 */
const VideoNodeView = (props) => {
  const { node, updateAttributes, editor } = props;
  const src = node.attrs.src;
  const transcodeId = node.attrs.transcodeId;

  const [transcodeState, setTranscodeState] = useState(null);

  useEffect(() => {
    if (src) return;            // already playable, no need to subscribe
    if (!transcodeId) return;   // nothing to look up

    const docRef = doc(db, 'videoTranscodes', transcodeId);
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          // The transcode function hasn't fired yet, or the doc was cleaned up.
          // Treat as "still processing" — the function writes 'processing' as
          // soon as it picks up the upload.
          setTranscodeState({ status: 'processing' });
          return;
        }
        const data = snap.data();
        setTranscodeState(data);
        if (data.status === 'complete' && data.outputUrl && editor && editor.isEditable) {
          // Persist the resolved URL into the editor's document so the saved
          // HTML carries it. We only do this when the editor is editable —
          // mutating the document in a readonly view would cause spurious
          // dirty state. The readonly path falls back to playing the URL
          // directly from `transcodeState` below.
          updateAttributes({ src: data.outputUrl });
        }
      },
      (err) => {
        setTranscodeState({ status: 'error', errorMessage: err.message });
      }
    );

    return () => unsub();
  }, [src, transcodeId, editor, updateAttributes]);

  const playableUrl =
    src ||
    (transcodeState && transcodeState.status === 'complete' && transcodeState.outputUrl) ||
    null;
  const progressPercent =
    transcodeState && Number.isFinite(transcodeState.progressPercent)
      ? Math.max(0, Math.min(100, Math.round(transcodeState.progressPercent)))
      : null;
  const hasProgress = progressPercent !== null;

  if (playableUrl) {
    return (
      <NodeViewWrapper className="rte-video-player">
        <ReactPlayer height="100%" url={playableUrl} controls={true} width="100%" />
      </NodeViewWrapper>
    );
  }

  if (transcodeState && transcodeState.status === 'error') {
    return (
      <NodeViewWrapper>
        <div className="video-placeholder is-error">
          <div className="video-placeholder__icon">
            <ErrorOutlineIcon fontSize="medium" />
          </div>
          <div className="video-placeholder__body">
            <div className="video-placeholder__title">Video processing failed</div>
            <div className="video-placeholder__detail">
              Please try uploading again. If it still fails, choose a shorter video or export it as MP4 first.
            </div>
          </div>
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      <div className="video-placeholder">
        <div className="video-placeholder__icon">
          <VideoFileIcon fontSize="medium" />
        </div>
        <div className="video-placeholder__body">
          <div className="video-placeholder__title">
            Processing video…{hasProgress ? ` ${progressPercent}%` : ''}
          </div>
          <div className="video-placeholder__detail">
            {hasProgress
              ? 'Converting your upload so it plays smoothly in any browser.'
              : "We're converting your upload so it plays smoothly in any browser. This usually takes a minute or two."}
          </div>
          <LinearProgress
            className="video-placeholder__bar"
            variant={hasProgress ? 'determinate' : 'indeterminate'}
            value={hasProgress ? progressPercent : undefined}
          />
        </div>
      </div>
    </NodeViewWrapper>
  );
};

export const Video = Node.create({
  name: 'video',
  group: 'block',
  selectable: true,
  draggable: true,
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src') || null,
        renderHTML: (attrs) => (attrs.src ? { src: attrs.src } : {}),
      },
      transcodeId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-transcode-id') || null,
        renderHTML: (attrs) =>
          attrs.transcodeId ? { 'data-transcode-id': attrs.transcodeId } : {},
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'video',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoNodeView)
  },
})
